import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'
import type {
  ChargeReportDrawerData,
  ChargeReportFilters,
  ChargeReportListResponse
} from '../types/chargeReport'

export const CHARGE_REPORT_QUERY_KEY = 'charge-reports'
export const CHARGE_REPORT_DRAWER_QUERY_KEY = 'charge-reports-drawer'

export function buildChargeReportQueryString(
  filters: ChargeReportFilters
): string {
  const params = new URLSearchParams()
  params.set('startTime', filters.startTime)
  params.set('endTime', filters.endTime)
  // Repeated `[]` params (not indexed) so the BE's qs parser keeps them as an
  // array regardless of count (indexed keys degrade to an object past ~20).
  filters.serviceUserIds?.forEach((id) => {
    params.append('serviceUserIds[]', id)
  })
  filters.fundingTypeIds?.forEach((id) => {
    params.append('fundingTypeIds[]', id)
  })
  if (filters.page !== undefined && filters.pageSize !== undefined) {
    params.set('pagination[page]', String(Math.max(1, filters.page)))
    params.set('pagination[limit]', String(filters.pageSize))
  }
  if (filters.sortField !== undefined && filters.sortDir !== undefined) {
    params.set(`sort[${filters.sortField}]`, String(filters.sortDir))
  }
  return params.toString()
}

export const useChargeReport = (
  filters: ChargeReportFilters | null,
  queryEnabled: boolean
) => {
  const qs = filters ? buildChargeReportQueryString(filters) : ''
  return useQuery<ChargeReportListResponse>(
    [CHARGE_REPORT_QUERY_KEY, filters],
    () =>
      getJSON({
        url: `charge-reports/?${qs}`,
        isRota: true
      }),
    {
      enabled: queryEnabled && Boolean(filters),
      keepPreviousData: true,
      staleTime: 60 * 1000,
      // Reconciling events (or rota-builder edits) changes the report, but those
      // flows don't invalidate this key. Refetch on mount so navigating back to
      // the report always shows fresh data instead of the cached counts.
      refetchOnMount: 'always'
    }
  )
}

export type ChargeReportDrawerParams = {
  rowId: string
  startTime: string
  endTime: string
  page: number
  limit: number
}

function buildDrawerUrl(params: ChargeReportDrawerParams): string {
  const search = new URLSearchParams()
  search.set('startTime', params.startTime)
  search.set('endTime', params.endTime)
  search.set('pagination[page]', String(Math.max(1, params.page)))
  search.set('pagination[limit]', String(params.limit))
  return `charge-reports/drawer/${params.rowId}?${search.toString()}`
}

export function useChargeReportDrawer(
  params: ChargeReportDrawerParams | null,
  enabled: boolean
) {
  return useQuery<ChargeReportDrawerData>(
    [CHARGE_REPORT_DRAWER_QUERY_KEY, params],
    () => {
      if (!params) {
        throw new Error('useChargeReportDrawer: params required')
      }
      return getJSON({
        url: buildDrawerUrl(params),
        isRota: true
      })
    },
    {
      enabled: Boolean(enabled && params),
      staleTime: 30 * 1000,
      keepPreviousData: true,
      // Keep the breakdown in step with the list — refetch when the drawer opens
      // so a just-reconciled event's figures aren't served from cache.
      refetchOnMount: 'always'
    }
  )
}
